
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Heart, MessageCircle, ExternalLink, Instagram, Facebook } from "lucide-react";
import { Badge } from "./ui/badge";

interface SocialMediaPost {
  id: string;
  platform: string;
  post_id: string;
  caption: string;
  media_url: string;
  media_type: string;
  post_url: string;
  likes_count: number;
  comments_count: number;
  posted_at: string;
  is_featured: boolean;
  is_active: boolean;
}

const SocialMediaPosts = () => {
  const [filterPlatform, setFilterPlatform] = useState("all");

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["social-media-posts", filterPlatform],
    queryFn: async () => {
      let query = supabase
        .from("sns_social_media_posts")
        .select("*")
        .eq("is_active", true)
        .order("posted_at", { ascending: false });

      if (filterPlatform !== "all") {
        query = query.eq("platform", filterPlatform);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SocialMediaPost[];
    },
  });

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "instagram":
        return <Instagram className="w-5 h-5 text-pink-500" />;
      case "facebook":
        return <Facebook className="w-5 h-5 text-blue-600" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-shell-dark">Follow Our Journey</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white rounded-lg h-80"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-gray-50" id="social-posts">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-4 text-shell-dark">
          Follow Our Journey
        </h2>
        <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
          Stay connected with our latest adventures and see what our guests are experiencing
        </p>

        <div className="flex justify-center mb-8">
          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {posts.map((post) => (
            <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative">
                {post.media_type === "video" ? (
                  <video
                    src={post.media_url}
                    className="w-full h-64 object-cover"
                    controls={false}
                    muted
                    poster={post.media_url}
                  />
                ) : (
                  <img
                    src={post.media_url}
                    alt="Social media post"
                    className="w-full h-64 object-cover"
                  />
                )}
                <div className="absolute top-2 left-2 flex items-center gap-2">
                  {getPlatformIcon(post.platform)}
                  {post.is_featured && (
                    <Badge className="bg-shell-primary">Featured</Badge>
                  )}
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                  {post.caption}
                </p>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      {post.likes_count}
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {post.comments_count}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(post.posted_at).toLocaleDateString()}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => window.open(post.post_url, "_blank")}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Post
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {posts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No posts found for the selected platform.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default SocialMediaPosts;
